import { create } from "zustand";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useAuth } from "@/store/auth";

export interface SessionModule {
  key: string;
  displayName: string;
  displayOrder: number;
}

/** The shape of `GET /api/v1/me` — everything the shell needs to build itself, in one call. */
export interface MeResponse {
  userId: string;
  email: string;
  tenantId: string;
  branchId?: string | null;
  employeeId?: string | null;
  departmentIds: string[];
  permissions: string[];
  enabledFeatures: string[];
  modules: SessionModule[];
  /**
   * True when this tenant's masters are projected from HealNTrack. Drives exactly one thing in the
   * UI: whether master screens render read-only. Nothing else branches on deployment mode.
   */
  isIntegratedWithHealNTrack: boolean;
}

interface SessionState {
  features: Set<string>;
  modules: SessionModule[];
  departmentIds: string[];
  isIntegrated: boolean;
  loaded: boolean;

  apply: (me: MeResponse) => void;
  reset: () => void;
  /** Whether a per-tenant feature is on. Unknown keys are off, never on. */
  featureOn: (key: string) => boolean;
}

export const useSession = create<SessionState>((set, get) => ({
  features: new Set<string>(),
  modules: [],
  departmentIds: [],
  isIntegrated: false,
  loaded: false,

  apply: (me) =>
    set({
      features: new Set(me.enabledFeatures),
      modules: [...me.modules].sort((a, b) => a.displayOrder - b.displayOrder),
      departmentIds: me.departmentIds,
      isIntegrated: me.isIntegratedWithHealNTrack,
      loaded: true,
    }),

  reset: () =>
    set({ features: new Set(), modules: [], departmentIds: [], isIntegrated: false, loaded: false }),

  featureOn: (key) => get().features.has(key),
}));

/**
 * Loads the session once the user is authenticated.
 *
 * <p>Long stale time on purpose: features and modules change when an administrator toggles them,
 * which is rare, and refetching on every window focus would put a request in front of every
 * tab-switch for a value that has not moved. <code>invalidate</code> after a toggle is what makes it
 * immediate.</p>
 */
export function useSessionLoader() {
  const isAuthenticated = useAuth((s) => Boolean(s.accessToken));
  const apply = useSession((s) => s.apply);

  return useQuery({
    queryKey: ["me"],
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data } = await api.get<MeResponse>("/api/v1/me");
      apply(data);
      return data;
    },
  });
}
